/**
 * Sanitize paragraph HTML to a minimal whitelist: b, br, u, i, em. strong is normalized to b.
 * span and all other tags are removed. Use for paragraphs, titles, accordion content.
 */
const ALLOWED_TAGS = /^(?:b|strong|br|u|i|em)$/;

export function sanitizeParagraphHtml(html: string): string {
  if (typeof html !== "string" || !html.trim()) return "";
  // Remove disallowed tags (including span)
  let out = html.replace(/<\/?([a-z][a-z0-9]*)(?:\s[^>]*)?\/?>/gi, (match, tagName) => {
    const tag = (tagName || "").toLowerCase();
    if (ALLOWED_TAGS.test(tag)) return match;
    return "";
  });
  // Strip attributes; normalize strong -> b
  out = out
    .replace(/<(strong)(?:\s[^>]*)?>/gi, "<b>")
    .replace(/<\/(strong)>/gi, "</b>")
    .replace(/<(b)(?:\s[^>]*)?>/gi, "<b>")
    .replace(/<\/(b)>/gi, "</b>")
    .replace(/<(br)(?:\s[^>]*)?\/?>/gi, "<br>")
    .replace(/<(u)(?:\s[^>]*)?>/gi, "<u>")
    .replace(/<\/(u)>/gi, "</u>")
    .replace(/<(i)(?:\s[^>]*)?>/gi, "<i>")
    .replace(/<\/(i)>/gi, "</i>")
    .replace(/<(em)(?:\s[^>]*)?>/gi, "<em>")
    .replace(/<\/(em)>/gi, "</em>");
  return out;
}

/**
 * Sanitize RICH body HTML (blog article / product / service bodies): the same
 * inline whitelist as {@link sanitizeParagraphHtml} PLUS block tags so multi-
 * paragraph bodies keep their structure (the paragraph whitelist strips <p>,
 * which collapsed whole article bodies into one run-on block). Attributes are
 * dropped; strong→b. Spacing for these block tags lives in `.rich-paragraph-block`.
 */
const ALLOWED_RICH_TAGS = /^(?:p|br|b|strong|u|i|em|ul|ol|li|h2|h3|h4|blockquote|a|table|thead|tbody|tr|td|th)$/;

export function sanitizeRichHtml(html: string): string {
  if (typeof html !== "string" || !html.trim()) return "";
  let out = html.replace(/<\/?([a-z][a-z0-9]*)(?:\s[^>]*)?\/?>/gi, (match, tagName) => {
    const tag = (tagName || "").toLowerCase();
    if (!ALLOWED_RICH_TAGS.test(tag)) return "";
    // Drop all attributes; normalize strong -> b. Keep the open/close form.
    const isClose = /^<\//.test(match);
    const norm = tag === "strong" ? "b" : tag;
    if (norm === "br") return "<br>";
    return isClose ? `</${norm}>` : `<${norm}>`;
  });
  return out;
}

/** Returns true if the string looks like HTML (contains a tag). */
/** Checks whether the string looks like HTML (contains a tag). */
export function isParagraphHtml(s: string): boolean {
  return typeof s === "string" && /<[a-z]/i.test(s);
}
